import { captureException } from '@sentry/nextjs'

import { getUserSubscriptionStatus } from '~/redux/store'

import { callApi } from '~/services/axiosInstance.service'

import { cleanNullAndUndefined2 } from '~/utils'

import { trackingEventStore } from '~/behavior-tracking/utility/tracking-events-store'

import appConfig from '~/config/app'

import { AppLanguage } from '~/types/locale'

import authService from '~/services/auth.service'

import { Event, ITrackingBQSchema, TrackingPlatform } from './types/analytics.type'

let pageReferrer: string

export default async function trackEventsBQ(event: Event & { userId?: number }): Promise<void> {
  // No product telemetry in the school platform. Deliberately separate from the gate in
  // `logEvent`: 46 call sites reach this function without passing through it, and the
  // *WithBQFallback ones fire precisely when consent is unresolved — which it permanently is here.
  if (appConfig.isSchoolPlatform) return

  try {
    trackingEventStore.resetSessionInterval()
    const processedEvent = await processEvent(event)

    await callApi({
      url: '/v1/process-events',
      method: 'POST',
      data: processedEvent,
    })
  } catch (e) {
    captureException(e, {
      tags: { source: 'TrackingBQ' },
      user: { id: event.userId },
      extra: { event },
      level: 'error',
    })
  }
}

async function processEvent(data: Event): Promise<ITrackingBQSchema> {
  delete data['gtm.uniqueEventId']
  const { event, user_id, product, page_title, page_uri, ...params } = data
  const trafficSource = getTrafficSource(data)

  const subscriptionStatus = getUserSubscriptionStatus()
  const pageLocation = window.location.href
  const pathname = page_uri ?? window.location.pathname
  const referrer = getPrevPage(data)
  const event_name = event === 'GApageview' ? 'page_view' : event
  const userId = user_id ?? authService.getUserId()
  const [session_id, visitor_id] = await Promise.all([
    trackingEventStore.getSessionID(),
    trackingEventStore.getPseudoId(),
  ])
  const geo = trackingEventStore.getGeoLocation()
  const { locale, path } = getLocaleAndPath(pathname)
  const visibility = document.visibilityState

  const page = {
    location: pageLocation,
    path,
    referrer,
    title: page_title || document?.title,
    product,
    language: locale,
    visibility,
  }

  const event_params = serializeEventParams(params)

  return cleanNullAndUndefined2({
    event_name,
    user_id: userId,
    event_timestamp: Date.now(),
    event_params,
    visitor_id,
    session_id,
    traffic_source: trafficSource,
    platform: TrackingPlatform.WEB,
    geo,
    subscription_status: subscriptionStatus,
    page,
  }) as ITrackingBQSchema
}

function getTrafficSource(data: Event): Record<string, string> | undefined {
  const params = data.page_uri ?? window.location.search
  const paramObject = new URLSearchParams(params)
  const ts: Record<string, string> = {}
  const paramName = ['utm_source', 'utm_medium', 'utm_name']
  paramObject.forEach((value, key) => {
    if (paramName.includes(key)) {
      const keyName = key.replace('utm_', '')
      ts[keyName] = value
    }
  })

  return Object.keys(ts).length ? ts : undefined
}

function getPrevPage(event: Event): string | undefined {
  const prevPage = pageReferrer ?? document.referrer

  if (event.event === 'GApageview') {
    pageReferrer = window.location.pathname + window.location.search
    return prevPage
  }
}

function getLocaleAndPath(url: string): { locale: string; path: string } {
  const match = url.match(/^\/(\w+)/)
  const languageCode = match?.[1] as AppLanguage
  const isValidLocaleCode = languageCode && Object.values(AppLanguage).includes(languageCode)

  const locale = isValidLocaleCode ? languageCode : AppLanguage.EN
  const path = isValidLocaleCode ? url.replace(`/${languageCode}`, '') : url

  return { locale, path }
}

function serializeEventParams(params: Record<string, any>): Array<{ key: string; value: string }> {
  const flattenParams = Object.entries(cleanNullAndUndefined2(params)).reduce((prev, current) => {
    const isObject = typeof current[1] === 'object' && current[1] !== null && !Array.isArray(current[1])

    if (isObject) {
      const nestedParams = Object.entries(current[1]) as Array<[string, any]>
      return prev.concat(nestedParams)
    }

    return [...prev, current]
  }, [] as Array<[string, any]>)

  const convertToKeyValuePair = flattenParams.map(([key, value]) => ({
    key: key.toString(),
    value: typeof value === 'object' ? JSON.stringify(value) : value.toString(),
  }))

  return convertToKeyValuePair
}
